import { invoke, isTauri } from "@tauri-apps/api/core";

/**
 * In-app auto-update for the desktop (Tauri) build. No-op on the web app
 * (app.steerly.ai), where `isTauri()` is false.
 *
 * On launch we check `downloads.steerly.ai/latest.json` (configured in
 * src-tauri/tauri.conf.json → plugins.updater). If a newer build is available we
 * ask the user, then download + install it and relaunch. The update artifact is
 * signed with the updater private key in CI; Tauri verifies the signature
 * against the public key baked into the app, so a tampered/forged update is
 * rejected before it's applied.
 *
 * The Tauri plugins are lazy-imported so the web bundle never evaluates their
 * IPC code (calling them outside Tauri would throw).
 */

// Guards against overlapping checks (e.g. the launch check racing a manual
// "Check for updates" click).
let isChecking = false;

// Remembers a version the user dismissed with "Later" so the silent launch
// check doesn't re-nag every start. A newer release has a different version
// string and prompts again; a manual check always prompts.
const SKIPPED_KEY = "steerly.updater.skippedVersion";

function readSkipped(): string | null {
  try {
    return localStorage.getItem(SKIPPED_KEY);
  } catch {
    return null;
  }
}
function writeSkipped(version: string | null): void {
  try {
    if (version === null) localStorage.removeItem(SKIPPED_KEY);
    else localStorage.setItem(SKIPPED_KEY, version);
  } catch {
    /* localStorage unavailable — fall back to prompting again next launch */
  }
}

export async function checkForUpdates(opts: { silentIfNone?: boolean } = {}): Promise<void> {
  if (!isTauri()) return;
  if (isChecking) return;
  isChecking = true;

  try {
    const [{ check }, { relaunch }, { ask, message }] = await Promise.all([
      import("@tauri-apps/plugin-updater"),
      import("@tauri-apps/plugin-process"),
      import("@tauri-apps/plugin-dialog"),
    ]);

    let update;
    try {
      update = await check();
    } catch (err) {
      console.warn("[updater] check failed", err);
      if (!opts.silentIfNone) {
        await message("Couldn't reach the update server. Please try again later.", {
          title: "Update check failed",
          kind: "warning",
        });
      }
      return;
    }

    if (!update) {
      if (!opts.silentIfNone) {
        await message("You're on the latest version of Steerly.", {
          title: "No updates",
          kind: "info",
        });
      }
      return;
    }

    // On a silent (launch) check, don't re-prompt for a version the user already
    // chose to skip. A manual check ignores the skip and always prompts.
    if (opts.silentIfNone && readSkipped() === update.version) return;

    const accepted = await ask(
      `Steerly ${update.version} is available (you have ${update.currentVersion}).\n\n` +
        "Download and install it now? Steerly will restart when it's done.",
      {
        title: "Update available",
        kind: "info",
        okLabel: "Install & Restart",
        cancelLabel: "Later",
      }
    );
    if (!accepted) {
      writeSkipped(update.version);
      return;
    }
    // They're updating now — clear any earlier skip.
    writeSkipped(null);

    // The bundle download can take a while; set expectations so the user doesn't
    // think the app froze (and knows it will restart on its own).
    await message("Downloading the update… Steerly will restart automatically when it's ready.", {
      title: "Updating Steerly",
      kind: "info",
    });

    try {
      // Installed by the app (install_update in src-tauri/src/main.rs), not by
      // update.downloadAndInstall(): on Windows the installer takes over by
      // ending the app, and only the app's own install stops the bundled host
      // before that, so the installer can replace it.
      await invoke("install_update", { version: update.version });
      await relaunch();
    } catch (err) {
      console.error("[updater] install failed", err);
      await message(`The update couldn't be installed: ${err}`, {
        title: "Update failed",
        kind: "error",
      });
    }
  } catch (err) {
    // The dynamic plugin imports or the dialog calls (ask/message) can throw —
    // e.g. an IPC failure. Swallow it here so the silent launch check (which
    // fires this via `void checkForUpdates()` in a setTimeout) never becomes an
    // unhandled promise rejection.
    console.error("[updater] update check failed", err);
  } finally {
    isChecking = false;
  }
}

/**
 * Fire a background update check shortly after launch (desktop only). Silent
 * when already up to date or on error, so it never interrupts a normal start.
 * Deferred so it doesn't compete with the workbench's initial render + IPC.
 */
export function scheduleLaunchUpdateCheck(): void {
  if (!isTauri()) return;
  setTimeout(() => {
    void checkForUpdates({ silentIfNone: true });
  }, 4000);
}
